import { isManager, query, requireClient, rpc } from "./supabase.js";
import { academyDateKey } from "../utils/date.js";
import { registrationPayload } from "./studentRegistration.js";
import { createExamRepository } from "./examSchedules.js";
export { prepareExamSave, examSchoolOptions } from "./examSchedules.js";
const examRepository = createExamRepository(rpc);
export const loadExamFolders = (folderId=null) => examRepository.load(folderId);
export const saveExamSchedule = (request) => examRepository.save(request);
export const getExamSaveResult = (requestId) => examRepository.result(requestId);
export const removeExamPeriod = (periodId) => examRepository.removePeriod(periodId);
export const removeExamFolder = (folderId) => examRepository.removeFolder(folderId);
import { classificationLabel, classificationPayload, sortCourseTerms } from "../utils/courseTerms.js";
export async function registerStudent(id, form, context) {
  return rpc("register_student_with_enrollments_v2", registrationPayload(id, form, isManager(context.role)));
}
export const confirmPendingEnrollment = (id, setup) => rpc("confirm_pending_enrollment", {
  target_pending_id: id, first_starts_on: setup.startDate, first_month_amount: setup.firstMonthAdjustment,
});
export const stages = {
  elementary: "초등",
  middle: "중등",
  high: "고등",
  other: "재수",
};
export const statuses = {
  enrolled: "재원",
  paused: "휴원",
  withdrawn: "퇴원",
  graduated: "졸업",
};
const reverse = (map, value) =>
  Object.keys(map).find((key) => map[key] === value);
const nullable = (value) =>
  value === "" || value === undefined ? null : value;
const phone = (value) => nullable(String(value || "").replace(/\D/g, ""));
export const weekdays = ["월", "화", "수", "목", "금", "토", "일"];
export function emptyState() {
  return {
    students: [],
    scores: [],
    consultations: [],
    events: [],
    archive: [],
    examFolders: [],
    timetableFolders: [],
    teachers: [],
    subjects: [],
    courseTerms: [],
    courseGroups: [],
    studentEnrollments: [],
    pendingStudentEnrollments: [],
    invoices: [],
    invoiceItems: [],
    paymentTransactions: [],
    refunds: [],
    scheduledAcademicYearChanges: [],
    academyInfo: {
      name: "",
      phone: "",
      address: "",
      operatingRegionIds: [],
      additionalSchoolNames: [],
      schools: [],
    },
  };
}
export function mapStudent(student, guardians = []) {
  const guardian =
    guardians.find((item) => item.isPrimary) ||
    guardians[0] ||
    student.primaryGuardian;
  return {
    id: student.id,
    name: student.name,
    gender: { male: "남", female: "여" }[student.gender] || "",
    schoolLevel: stages[student.schoolStage],
    school: student.schoolName || "",
    grade: String(student.grade ?? ""),
    className: student.schoolClass || "",
    phone: student.phone ?? student.phoneMasked ?? "",
    phoneMasked: student.phoneMasked,
    joinedAt: student.registeredOn,
    status:
      statuses[
        student.statusAtDate ?? student.status ?? student.currentStatusCache
      ] || "",
    memo: student.memo || "",
    billingMethod: student.defaultPaymentMethod || "",
    parentName: guardian?.name || "",
    parentPhone: guardian?.phone ?? guardian?.phoneMasked ?? "",
    guardianId: guardian?.id,
    guardianRelationship: guardian?.relationship || "보호자",
    smsConsent: guardian?.smsConsent ?? false,
    billingVisible: student.billingVisible,
  };
}
function mapEnrollment(item, studentId) {
  return {
    id: item.id,
    studentId,
    courseGroupId: item.courseGroup.id,
    courseId: item.courseGroup.id,
    startDate: item.startsOn,
    endDate: item.endsOn || "",
    appliedTuition: item.appliedTuition,
    firstMonthAdjustment: item.firstMonthAmount,
    endReason: item.endReason || "",
    endReasonNote: "",
    memo: item.memo || "",
    active: item.periodStatus === "current",
    billingVisible: item.billingVisible,
  };
}
async function rows(table, context, columns = "*") {
  const result = [];
  let offset = 0;
  while (true) {
    const page = await query(
      requireClient()
        .from(table)
        .select(columns)
        .eq("academy_id", context.academyId)
        .is("deleted_at", null)
        .order("id")
        .range(offset, offset + 499),
    );
    result.push(...page);
    if (page.length < 500) return result;
    offset += 500;
  }
}
export async function loadAcademy(context) {
  const state = emptyState();
  if (context.role === "kiosk") return state;
  const asOfDate = academyDateKey();
  const [
    settings,
    events,
    examFolders,
    files,
    archiveFolders,
    archiveItems,
    studentRevisions,
    schools,
  ] = await Promise.all([
    rpc("list_basic_settings", { include_inactive: true }),
    rows("calendar_events", context),
    loadExamFolders(),
    rows("files", context),
    rows("archive_folders", context),
    rows("archive_items", context),
    rows("students", context, "id,updated_at"),
    rpc("list_academy_schools"),
  ]);
  const studentRevisionById = new Map(
    studentRevisions.map((item) => [item.id, item.updated_at]),
  );
  state.academyInfo = { ...state.academyInfo, ...context.academy };
  state.academyInfo.schools = schools;
  state.teachers = settings.teachers.map((item) => ({
    ...item,
    active: item.isActive,
  }));
  state.subjects = settings.subjects.map((item) => ({
    ...item,
    active: item.isActive,
  }));
  state.courseTerms = sortCourseTerms(settings.courseTerms.map((item) => ({
    ...item,
    value: item.id,
    label: `${item.academicYear} ${classificationLabel(item.termType)}`,
    active: item.isActive,
  })));
  state.courseGroups = settings.courseGroups.map((item) => {
    const term = settings.courseTerms.find(
      (candidate) => candidate.id === item.courseTermId,
    );
    return {
      ...item,
      className: item.name,
      schoolLevel: stages[item.schoolStage],
      grade: String(item.grade ?? ""),
      days: item.weekdays.map((day) => weekdays[day - 1]),
      day: weekdays[item.weekdays[0] - 1] || "",
      startTime: item.startsAt?.slice(0, 5),
      endTime: item.endsAt?.slice(0, 5),
      clinicDays: (item.clinicWeekdays || []).map((day) => weekdays[day - 1]),
      clinicStartTime: item.clinicStartsAt?.slice(0, 5) || "",
      clinicEndTime: item.clinicEndsAt?.slice(0, 5) || "",
      tuition: item.defaultTuition,
      year: String(term?.academicYear || ""),
      term: item.courseTermId,
      termType: term?.termType,
      termLabel: state.courseTerms.find(
        (candidate) => candidate.id === item.courseTermId,
      )?.label,
      courseType: item.classType === "intensive" ? "special" : "regular",
      active: item.isActive,
    };
  });
  let cursor = null;
  do {
    const page = await rpc("list_students", {
      as_of_date: asOfDate,
      page_size: 50,
      ...(cursor ? { after_name: cursor.name, after_id: cursor.id } : {}),
    });
    state.students.push(
      ...page.items.map((item) => ({
        ...mapStudent(item),
        updatedAt: studentRevisionById.get(item.id),
      })),
    );
    cursor = page.hasMore ? page.nextCursor : null;
  } while (cursor);
  if (isManager(context.role)) {
    // Owner/admin need current enrollments for the existing class roster. Keep requests bounded.
    for (let index = 0; index < state.students.length; index += 10) {
      const details = await Promise.all(
        state.students
          .slice(index, index + 10)
          .map((item) =>
            rpc("get_student_detail", {
              target_student_id: item.id,
              as_of_date: asOfDate,
            }),
          ),
      );
      details.forEach((detail) => {
        state.studentEnrollments.push(
          ...detail.enrollments.map((item) =>
            mapEnrollment(item, detail.student.id),
          ),
        );
      });
    }
  }
  state.events = events.map((item) => ({
    id: item.id,
    title: item.title,
    date: new Date(item.starts_at).toLocaleDateString("sv-SE", {
      timeZone: "Asia/Seoul",
    }),
    endDate: new Date(
      new Date(item.ends_at).getTime() - (item.is_all_day ? 86400000 : 0),
    ).toLocaleDateString("sv-SE", { timeZone: "Asia/Seoul" }),
    time: item.is_all_day
      ? ""
      : new Intl.DateTimeFormat("sv-SE", {
          timeZone: "Asia/Seoul",
          hour: "2-digit",
          minute: "2-digit",
        }).format(new Date(item.starts_at)),
    color: item.color,
    memo: item.memo || "",
  }));
  state.examFolders = examFolders;
  state.archive = archiveFolders.map((folder) => ({
    id: folder.id,
    year: folder.name.match(/^\d{4}/)?.[0] || "",
    school: folder.school_name || "",
    grade: String(folder.grade ?? ""),
    subject: folder.subject_name || "",
    semester: folder.term_type || "",
    examKind: folder.exam_type || "",
    description: "",
    createdAt: folder.created_at,
    files: archiveItems
      .filter((item) => item.folder_id === folder.id)
      .flatMap((item) => {
        const file = files.find((candidate) => candidate.id === item.file_id);
        return file
          ? [
              {
                id: file.id,
                archiveItemId: item.id,
                name: file.original_filename,
                type: file.mime_type,
                size: file.byte_size,
              },
            ]
          : [];
      }),
  }));
  return state;
}
export async function loadStudent(studentId, state) {
  const detail = await rpc("get_student_detail", {
    target_student_id: studentId,
    as_of_date: academyDateKey(),
  });
  let pending = [];
  try { pending = await rpc("list_pending_enrollments", { target_student_id: studentId }); }
  catch (failure) { if (!['PGRST202', '42883'].includes(failure.code)) throw failure; }
  const scores = [];
  let cursor = null;
  do {
    const page = await rpc("list_student_scores", {
      target_student_id: studentId,
      page_size: 50,
      ...(cursor
        ? { after_taken_on: cursor.takenOn, after_id: cursor.id }
        : {}),
    });
    scores.push(
      ...page.items.map((item) => ({
        id: item.id,
        studentId,
        title: item.examName,
        subject: item.subject.name,
        subjectId: item.subject.id,
        date: item.takenOn,
        score: item.score,
        maxScore: item.maxScore,
        level: item.grade,
        comment: item.comment,
        files: [],
      })),
    );
    cursor = page.hasMore ? page.nextCursor : null;
  } while (cursor);
  const consultations = await query(
    requireClient()
      .from("consultations")
      .select("id,student_id,consulted_at,consultation_type,content,tags")
      .eq("student_id", studentId)
      .is("deleted_at", null)
      .order("consulted_at", { ascending: false }),
  );
  const files = await query(
    requireClient()
      .from("files")
      .select("id,target_type,target_id,original_filename,mime_type,byte_size")
      .is("deleted_at", null)
      .in("target_id", [studentId, ...consultations.map((item) => item.id)]),
  );
  const mapFiles = (targetId) =>
    files
      .filter((file) => file.target_id === targetId)
      .map((file) => ({
        id: file.id,
        name: file.original_filename,
        type: file.mime_type,
        size: file.byte_size,
      }));
  return {
    ...state,
    students: state.students.map((item) =>
      item.id === studentId
        ? {
            ...item,
            ...mapStudent(detail.student, detail.guardians),
            phoneMasked: item.phoneMasked,
          }
        : item,
    ),
    studentEnrollments: [
      ...state.studentEnrollments.filter(
        (item) => item.studentId !== studentId,
      ),
      ...detail.enrollments.map((item) => mapEnrollment(item, studentId)),
    ],
    pendingStudentEnrollments: [
      ...(state.pendingStudentEnrollments || []).filter(item => item.studentId !== studentId),
      ...pending.map(item => ({ ...mapEnrollment(item, studentId), pending: true })),
    ],
    scores,
    consultations: consultations.map((item) => ({
      id: item.id,
      studentId,
      date: new Date(item.consulted_at)
        .toLocaleString("sv-SE", { timeZone: "Asia/Seoul" })
        .replace(" ", "T")
        .slice(0, 16),
      type: item.consultation_type,
      content: item.content,
      tags: item.tags,
      files: mapFiles(item.id),
    })),
  };
}
async function saveRow(
  table,
  id,
  payload,
  exists,
  context,
  expectedUpdatedAt = null,
) {
  const client = requireClient();
  let request = exists
    ? client
        .from(table)
        .update(payload)
        .eq("id", id)
        .eq("academy_id", context.academyId)
    : client
        .from(table)
        .insert({ id, academy_id: context.academyId, ...payload });
  if (exists && expectedUpdatedAt) {
    request = request.eq("updated_at", expectedUpdatedAt);
    const saved = await query(request.select("id,updated_at").maybeSingle());
    if (!saved) {
      throw new Error(
        "다른 사용자가 먼저 수정했습니다. 최신 정보를 다시 불러왔습니다.",
      );
    }
    return saved;
  }
  return query(request.select("id").single());
}
export async function uploadFiles(files, targetType, targetId, context) {
  const saved = [];
  for (const file of files || []) {
    if (file.id) {
      saved.push(file);
      continue;
    }
    const path = `${context.academyId}/${context.userId}/${crypto.randomUUID()}`;
    const uploaded = await requireClient()
      .storage.from("academy-files")
      .upload(path, file.raw, { contentType: file.type, upsert: false });
    if (uploaded.error)
      throw new Error("파일을 업로드하지 못했습니다. 다시 시도해 주세요.");
    try {
      const id = await rpc("complete_file_upload", {
        input_object_path: path,
        input_original_filename: file.name,
        input_extension: file.name.split(".").pop().toLowerCase(),
        input_mime_type: file.type,
        input_byte_size: file.size,
        input_target_type: targetType,
        input_target_id: targetId,
      });
      saved.push({ id, name: file.name, type: file.type, size: file.size });
    } catch (error) {
      await rpc("record_file_upload_orphan", {
        input_object_path: path,
        input_failure_reason: "metadata_failed",
      });
      throw error;
    }
  }
  return saved;
}
export async function persistChanges(before, after, context) {
  if (context.role === "kiosk")
    throw new Error("이 작업에 대한 권한이 없습니다.");
  if (
    JSON.stringify(before.scheduledAcademicYearChanges) !==
    JSON.stringify(after.scheduledAcademicYearChanges)
  )
    throw new Error("학년 변경 예약은 현재 서버에서 지원하지 않습니다.");
  if (
    JSON.stringify(before.academyInfo) !== JSON.stringify(after.academyInfo)
  ) {
    throw new Error("현재 서버는 학원 기본정보 수정 API를 제공하지 않습니다.");
  }

  if (
    before.students.some(
      (item) => !after.students.some((next) => next.id === item.id),
    )
  )
    throw new Error(
      "현재 서버는 학생 기록 정리 API를 제공하지 않습니다. 상태 변경은 저장할 수 있습니다.",
    );
  for (const key of ["studentEnrollments", "archive"]) {
    if (
      before[key].some(
        (item) => !after[key].some((next) => next.id === item.id),
      )
    )
      throw new Error("현재 서버는 이 기록의 삭제 API를 제공하지 않습니다.");
  }
  for (const item of after.students) {
    const old = before.students.find((old) => old.id === item.id);
    if (old?.parentPhone && !item.parentPhone)
      throw new Error(
        "보호자 삭제는 다른 보호자를 주 보호자로 바꾼 뒤에 가능합니다. 현재 화면은 한 명의 주 보호자만 관리하므로 연락처를 수정해 주세요.",
      );
  }
  for (const item of after.scores) {
    if (item.files?.some((file) => !file.id))
      throw new Error("성적별 첨부파일 연결 계약이 없어 업로드할 수 없습니다.");
  }
  for (const item of after.archive) {
    const old = before.archive.find((old) => old.id === item.id);
    if (
      old?.files.some((file) => !item.files.some((next) => next.id === file.id))
    )
      throw new Error(
        "현재 서버는 기출 파일 연결 삭제 API를 제공하지 않습니다.",
      );
  }
  if (before.examFolders.some((folder)=>!after.examFolders.some((next)=>next.id===folder.id)))
    throw new Error("시험 폴더는 전용 삭제 기능으로 삭제해 주세요.");
  for (const folder of after.examFolders) {
    const old = before.examFolders.find((old) => old.id === folder.id);
    if (
      JSON.stringify(old?.periods || []) !== JSON.stringify(folder.periods)
    )
      throw new Error("시험 일정은 학교·학년을 선택한 전용 저장 기능으로 변경해 주세요.");
  }
  const specs = {
    teachers: [
      "teacher",
      async (item, existing) =>
        rpc("save_teacher_setting", {
          setting_id: existing ? item.id : null,
          setting_name: item.name,
          setting_display_name: item.displayName || item.name,
          setting_phone: phone(item.phone),
          setting_memo: nullable(item.memo),
          setting_is_active: item.active !== false,
        }),
    ],
    subjects: [
      "subject",
      async (item, existing) =>
        rpc("save_subject_setting", {
          setting_id: existing ? item.id : null,
          setting_name: item.name,
          setting_is_active: item.active !== false,
        }),
    ],
    courseTerms: [
      "course_term",
      async (item, existing) => {
        if (!isManager(context.role)) throw new Error("학기 설정 권한이 없습니다.");
        return rpc("save_course_term_classification", classificationPayload(item, existing));
      },
    ],
    courseGroups: [
      "course_group",
      async (item, existing) => {
        if (!isManager(context.role))
          throw new Error("이 작업에 대한 권한이 없습니다.");
        return rpc("save_course_group_classified_v2", {
          setting_id: existing ? item.id : null,
          setting_academic_year: Number(item.year),
          setting_term_type: item.termType,
          setting_subject_id: item.subjectId,
          setting_teacher_id: item.teacherId,
          setting_name: item.className,
          setting_school_stage: reverse(stages, item.schoolLevel),
          setting_grade: Number(item.grade) || null,
          setting_weekdays: (item.days?.length ? item.days : [item.day])
            .filter(Boolean)
            .map((day) => weekdays.indexOf(day) + 1).sort((a, b) => a - b),
          setting_starts_at: item.startTime,
          setting_ends_at: item.endTime,
          setting_capacity: Number(item.capacity || 0),
          setting_default_tuition: Number(item.tuition || 0),
          setting_class_type:
            item.courseType === "special" ? "intensive" : "regular",
          setting_is_active: item.active !== false,
          setting_clinic_weekdays: (item.clinicDays || [])
            .map((day) => weekdays.indexOf(day) + 1).sort((a, b) => a - b),
          setting_clinic_starts_at: nullable(item.clinicStartTime),
          setting_clinic_ends_at: nullable(item.clinicEndTime),
        });
      },
    ],
  };
  for (const [key, [kind, save]] of Object.entries(specs)) {
    for (const item of after[key]) {
      const old = before[key].find(
        (candidate) =>
          (candidate.id || candidate.value) === (item.id || item.value),
      );
      if (JSON.stringify(old) !== JSON.stringify(item))
        await save(item, Boolean(old));
    }
    for (const item of before[key].filter(
      (old) =>
        !after[key].some(
          (next) => (next.id || next.value) === (old.id || old.value),
        ),
    ))
      await rpc("deactivate_basic_setting", {
        setting_kind: kind,
        setting_id: item.id,
      });
  }
  for (const item of after.students) {
    const old = before.students.find((candidate) => candidate.id === item.id);
    if (JSON.stringify(old) === JSON.stringify(item)) continue;
    const payload = {
      name: item.name.trim(),
      gender: nullable({ 남: "male", 여: "female" }[item.gender]),
      school_stage: reverse(stages, item.schoolLevel),
      school_name: nullable(item.school?.trim()),
      grade: Number(item.grade) || null,
      school_class: nullable(item.className),
      phone: phone(item.phone),
      registered_on: item.joinedAt,
      memo: nullable(item.memo),
      ...(isManager(context.role)
        ? { default_payment_method: nullable(item.billingMethod) }
        : {}),
    };
    await saveRow(
      "students",
      item.id,
      payload,
      Boolean(old),
      context,
      old?.updatedAt,
    );
    if ((!old && item.status !== "재원") || (old && old.status !== item.status))
      await rpc("change_student_status", {
        target_student_id: item.id,
        new_status: reverse(statuses, item.status),
        // Student status follows the academy operating day in Asia/Seoul.
        effective_on: academyDateKey(),
        reason: "학생 정보에서 상태 변경",
      });
    if (
      item.parentPhone &&
      (!old ||
        item.parentPhone !== old.parentPhone ||
        item.parentName !== old.parentName ||
        item.guardianRelationship !== old.guardianRelationship)
    ) {
      const id = item.guardianId || crypto.randomUUID();
      await saveRow(
        "guardians",
        id,
        {
          student_id: item.id,
          name: item.parentName?.trim() || "보호자",
          relationship: item.guardianRelationship || "보호자",
          phone: phone(item.parentPhone),
          is_primary: true,
          sms_consent: item.smsConsent ?? false,
        },
        Boolean(item.guardianId),
        context,
      );
    }
  }
  const collections = {
    studentEnrollments: [
      "enrollments",
      (item, old) => ({
        student_id: item.studentId,
        course_group_id: item.courseGroupId,
        starts_on: item.startDate,
        ends_on: nullable(item.endDate),
        end_reason: item.endDate
          ? [item.endReason, item.endReasonNote].filter(Boolean).join(" · ")
          : null,
        memo: nullable(item.memo),
        ...(isManager(context.role)
          ? {
              applied_tuition: Number(item.appliedTuition),
              first_month_adjustment:
                item.firstMonthAdjustment === ""
                  ? null
                  : item.firstMonthAdjustment,
            }
          : old
            ? {}
            : {
                applied_tuition: item.appliedTuition,
                first_month_adjustment: item.firstMonthAdjustment || null,
              }),
      }),
    ],
    scores: [
      "scores",
      (item) => ({
        student_id: item.studentId,
        subject_id:
          item.subjectId ||
          after.subjects.find((subject) => subject.name === item.subject)?.id,
        exam_name: item.title.trim(),
        taken_on: item.date,
        score: item.score,
        max_score: nullable(item.maxScore),
        grade: nullable(item.level),
        comment: nullable(item.comment),
      }),
    ],
    consultations: [
      "consultations",
      (item) => ({
        student_id: item.studentId,
        consulted_at: new Date(item.date).toISOString(),
        consultation_type: item.type,
        content: item.content.trim(),
        tags: item.tags || [],
      }),
    ],
    events: [
      "calendar_events",
      (item) => {
        const start = new Date(`${item.date}T${item.time || "00:00"}:00+09:00`);
        const end = new Date(
          `${item.endDate || item.date}T${item.time || "00:00"}:00+09:00`,
        );
        end.setTime(end.getTime() + (item.time ? 3600000 : 86400000));
        return {
          title: item.title,
          starts_at: start.toISOString(),
          ends_at: end.toISOString(),
          is_all_day: !item.time,
          event_type: "academy",
          color: nullable(item.color),
          memo: nullable(item.memo),
        };
      },
    ],
  };
  for (const [key, [table, payload]] of Object.entries(collections)) {
    for (const item of after[key]) {
      const old = before[key].find((candidate) => candidate.id === item.id);
      if (JSON.stringify(old) === JSON.stringify(item)) continue;
      await saveRow(table, item.id, payload(item, old), Boolean(old), context);
      if (key === "consultations") {
        await uploadFiles(item.files, "consultation", item.id, context);
        for (const file of (old?.files || []).filter(
          (file) => file.id && !item.files?.some((next) => next.id === file.id),
        ))
          await rpc("soft_delete_file", { target_file_id: file.id });
      }
    }
    for (const item of before[key].filter(
      (old) => !after[key].some((next) => next.id === old.id),
    )) {
      if (
        !after.students.some((student) => student.id === item.studentId) &&
        key !== "events"
      )
        continue;
      if (key === "studentEnrollments")
        throw new Error("현재 서버는 수강 기록 삭제 API를 제공하지 않습니다.");
      await rpc(
        {
          scores: "soft_delete_score",
          consultations: "soft_delete_consultation",
          events: "soft_delete_calendar_event",
        }[key],
        {
          [key === "scores"
            ? "score_id"
            : key === "consultations"
              ? "consultation_id"
              : "calendar_event_id"]: item.id,
        },
      );
    }
  }
  await persistExams(before, after, context);
  await persistArchives(before, after, context);
}
async function persistExams(before, after, context) {
  // Period saves use saveExamSchedule; never perform sequential period/child DML.
  for (const folder of after.examFolders) {
    const old = before.examFolders.find((item) => item.id === folder.id);
    if (!old || old.title !== folder.title || old.year !== folder.year)
      await saveRow("exam_folders", folder.id,
        { name: folder.year + " · " + folder.title }, Boolean(old), context);
  }
}
async function persistArchives(before, after, context) {
  for (const item of after.archive) {
    const old = before.archive.find((candidate) => candidate.id === item.id);
    if (JSON.stringify(old) === JSON.stringify(item)) continue;
    const payload = {
      school_name: nullable(item.school),
      grade: Number(item.grade) || null,
      subject_name: nullable(item.subject),
      term_type: nullable(item.semester),
      exam_type: nullable(item.examKind),
    };
    await saveRow(
      "archive_folders",
      item.id,
      {
        ...payload,
        name: `${item.year} · ${item.school} ${item.grade} ${item.subject} ${item.semester} ${item.examKind}`.trim(),
      },
      Boolean(old),
      context,
    );
    const files = await uploadFiles(item.files, "archive", item.id, context);
    for (const file of files)
      await saveRow(
        "archive_items",
        file.archiveItemId || crypto.randomUUID(),
        { ...payload, folder_id: item.id, file_id: file.id },
        Boolean(file.archiveItemId),
        context,
      );
  }
}
